import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, FlaskConical, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const DetailedMethodology = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="w-full bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl overflow-hidden">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 md:p-5 text-left group hover:bg-muted/30 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-[hsl(var(--brand-purple))]" />
          <span className="font-bold text-foreground text-base md:text-lg">
            Methodology
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 md:px-5 pb-5 space-y-6 animate-fade-in">
          {/* What We Analyze */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground text-sm md:text-base border-b border-border/50 pb-2">
              What We Analyze
            </h3>
            <p className="text-sm text-muted-foreground">
              This scan performs three key checks:
            </p>

            <div className="space-y-4">
              {/* AI Visibility Analysis */}
              <div className="bg-muted/30 rounded-lg p-3 md:p-4">
                <h4 className="font-semibold text-foreground text-sm flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-gradient-to-r from-primary to-[hsl(var(--brand-purple))] flex items-center justify-center text-primary-foreground text-xs font-bold">1</span>
                  AI Visibility Analysis
                </h4>
                <p className="text-sm text-muted-foreground">
                  We query OpenAI's Responses API with web search enabled (model: gpt-4.1-2025-04-14) using industry-specific prompts asking for top brands in your category. We check if your brand appears in the response and in what context.
                </p>
              </div>

              {/* Platform-Specific Checks */}
              <div className="bg-muted/30 rounded-lg p-3 md:p-4">
                <h4 className="font-semibold text-foreground text-sm flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-gradient-to-r from-primary to-[hsl(var(--brand-purple))] flex items-center justify-center text-primary-foreground text-xs font-bold">2</span>
                  Platform-Specific Checks
                </h4>
                <p className="text-sm text-muted-foreground">
                  Real-time verification on ChatGPT and Google Search.
                </p>
              </div>

              {/* Google Search Rankings */}
              <div className="bg-muted/30 rounded-lg p-3 md:p-4">
                <h4 className="font-semibold text-foreground text-sm flex items-center gap-2 mb-2">
                  <span className="w-5 h-5 rounded-full bg-gradient-to-r from-primary to-[hsl(var(--brand-purple))] flex items-center justify-center text-primary-foreground text-xs font-bold">3</span>
                  Google Search Rankings
                </h4>
                <p className="text-sm text-muted-foreground">
                  We search for your target keyword (generic search) and check your domain's ranking position in organic results.
                </p>
              </div>
            </div>
          </div>

          {/* How We Score */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground text-sm md:text-base border-b border-border/50 pb-2">
              How We Score
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-primary/10 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-primary">40 pts</div>
                <div className="text-xs text-muted-foreground">ChatGPT Visibility</div>
                <div className="text-xs text-muted-foreground/70">✓ Scanned</div>
              </div>
              <div className="bg-[hsl(var(--brand-purple)/0.1)] rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-[hsl(var(--brand-purple))]">30 pts</div>
                <div className="text-xs text-muted-foreground">Google Search</div>
                <div className="text-xs text-muted-foreground/70">✓ Scanned</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Maximum score 70. These are the only two platforms the scan measures.
            </p>

          </div>

          {/* Data Sources */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground text-sm md:text-base border-b border-border/50 pb-2">
              Data Sources
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent" />
                OpenAI Responses API, model gpt-4.1-2025-04-14 (real-time)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent" />
                SerpAPI (live Google results)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent" />
                Platform-specific checks (manual verification)
              </li>
            </ul>
            <p className="text-xs text-muted-foreground italic pt-2">
              All data is collected at scan time, not from cached estimates.
            </p>
          </div>

          {/* View Full Methodology Link */}
          <div className="flex justify-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/methodology')}
              className="gap-1.5 text-sm border-[hsl(var(--brand-purple))] text-[hsl(var(--brand-purple))] hover:bg-[hsl(var(--brand-purple)/0.15)]"
            >
              View Full Methodology
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedMethodology;
